---
title: Advanced authoring in the Workshop
summary: The Workshop's advanced tools — capability cards for skills, tools, and workflows, raw bundle JSON, and the Review pass.
visibility: authenticated
audience: author-admin
section: Advanced authoring
order: 95
last_verified: 2026-07-21
owner: docs
---

# Advanced authoring in the Workshop

The **Persona Workshop** is where you shape a persona — its identity, personality,
rules, and knowledge — and it also carries the advanced authoring tools that used to
live in the retired Visual Composer: capability cards, a raw JSON editor, and
review/preview checks.

## Open it

On the **Personas** page, click a persona's **Edit** button — it opens the Workshop
directly. Everything below assumes the Workshop is open for one persona.

## Simple vs. Advanced

The Workshop opens in **Simple** mode. Flip the **Simple ⇄ Advanced** toggle (top of
the page) to reveal the advanced surfaces:

- The section showing what the persona can do swaps its read-only summary for the
  full editing cards: **Skills**, **Tools**, and **Workflows**. Each card has its own
  guide: [Add skills](/help/advanced/add-skills), [Add tools](/help/advanced/add-tools),
  [Add workflows](/help/advanced/add-workflows).
- The **Advanced** section gains **Raw bundle JSON** — a plain editor over the
  persona's entire bundle, for fields the cards don't expose. *Apply* stages your
  change as an unsaved draft; **Save** in the top bar commits it.

## Inspect the composed prompt

The **Advanced** section (visible in both Simple and Advanced mode) has a **View
composed prompt →** button. It opens a read-only breakdown of the exact prompt this
persona sends — section by section, with token estimates and the runtime layers added
per conversation — plus a **Raw** tab with the exact string sent to the model. Each
authored section has a pencil icon that jumps you to the block that produced it (for
example, a **Rules & constraints** block); the inspector itself doesn't accept edits —
change the wording at the block, then reopen the inspector to see the result.

## Save, and the "save to test" rule

Changes are staged as a draft until you click **Save** in the top bar. Preview and
Review run against the **saved** persona — save first when in doubt.

## Coach, Preview, and Review

The right-hand rail has three tabs:

- **💬 Coach** — describe the change you want in plain language and apply the
  coach's proposals to your draft.
- **Preview** — a live sample conversation. The reply previews automatically when
  the persona is saved and error-free; you can also type your own sample message.
- **Review** — an AI review of the persona's prompt, plus a workflow-fit check and
  a wiring subsection that lists structural problems (for example, a skill that
  needs a capability you haven't bound). Findings come with apply-able fixes and
  jump links to the block they concern.

## See also

- [Personas, bundles, and workflows](/help/concepts/personas-bundles-workflows) — the model behind the cards.
